import React from "react";
import {Container} from "react-bootstrap";
import {Link} from "react-router-dom";

function TheLifecycleOfSoftwareObjects() {
    return (
        <Container>
            <h5 className="author-label">
                <Link to="/reading/the-lifecycle-of-software-objects">
                    The Lifecycle Of Software Objects
                </Link>
            </h5>
            <p className="text-center"><strong>Ted Chiang</strong></p>
            <p><em>2024-01-13</em></p>
            <p>
                Ted Chiang’s novella <em>The Lifecycle of Software Objects</em> struck me as a nuanced
                exploration of how technology mediates and ultimate constructs family dynamics.
            </p>
            <p>
                While mainly focused on the way a new category of relationship can be created by a new technology,
                the story also explores how existing family structures
                and sexual relationships can be affected by the changing technological landscape.
            </p>
            <h5>Synopsis</h5>
            <p><em>(may contain spoilers)</em></p>
            <p>
                The story follows two owners of “digients”, AI-powered pets that exist in virtual reality.
                What starts as a hobby evolves into a central facet of life for owners of these digital companions,
                who spend vast sums on tutoring and hardware for their digients.
            </p>
            <p>
                Chiang doesn’t seem to take a stance on whether the digients are charming or sinister.
                Though it’s depressing that Ana, who went to school as a primate specialist only to find
                most species of primates going extinct & losing her job as a zookeeper, has to take refuge in
                virtual creatures, it's hard not to fall in love with her digient Jax, who learns to communicate
                with human teenagers and joins a community focused on improvised dancing with 4-armed VR avatars.
            </p>
            <p>
                The digients also have a major impact on their owners’ human relationships. One of the protagonists
                falls out of love with his wife, who doesn’t understand the caregiver relationship he has with his two
                virtual talking pandas Marco and Polo. He develops an unhealthy fixation on Ana, his fellow
                digient-lover, who herself struggles to find a partner who isn’t bothered by the big role her
                pet plays in her life.
            </p>
            <p>
                In a subtle touch, it’s implied that many real animals are going extinct, and even humans are
                increasingly relying on fertility treatments and cloning to reproduce—while the book is filled with sex
                and reproduction, there is not a single instance of a male and female of the same species conceiving a
                child through unaided biological means.
            </p>
            <p>
                In the book, corporations mediate every aspect of the family—for the digient
                owners, their continued relationship which their pet (which is really more like their child) depends on
                the upkeep of a VR platform. When the platform Jax inhabits goes out of business, Ana’s life is
                completely upended.
            </p>
            <h5>Aren't Americans really weird about their dogs?</h5>
            <p>
                While the technology in Chiang's novella isn't too dissimilar to AI software that's actually being
                developed (such as LLM-based virtual companions), the digients seemed most similar to the relatively
                novel situation of dogs and cats.
            </p>
            <p>
                The concept of "animal welfare", the institution of animal shelters, puppy mills, breeders--it's all
                very new, and deeply influenced by the "technology" that structures our neighborhoods and daily
                lives.
            </p>
            <p>
                Is it a good or a bad thing that as economies modernize & as people get more access to birth control
                and education, more and more people choose to have pets instead of kids? As in Chiang's story, the
                situation is a complex mix of the tragic and the hopeful.
            </p>
            <p>
                It's sad that the world's largest country just lost millions of humans from its population; on the other
                hand, modern technology allows people unprecedented control over their own bodies & their own futures...
                and the use of pets creates opportunities for small number of animals to experience an incredibly
                lavish lifestyle, with luxury resorts that offer canine spa treatments & restaurants offering gourmet
                meals for humans and their animal companions to share together. (Yes, that last one is a real thing that
                exists down the block from my mom's apartment).
            </p>
            <p>
                As a pet owner, I can empathise with Chiang's characters Ana and Derek as they struggle to form normal
                human relationships after experiencing a deep connection with their new virtual companion.
            </p>
            <p>
                Prior to recent decades, I don't know if it was even <em>possible</em> to live the lifestyle I'm
                currently living, where all work is done remotely, & all direct physical interaction is only with
                non-humans such as dogs, cats, plants, insects, and self-checkout machines. Chiang's portrayal of a
                world were all interaction happens within a handful of popular Virtual Reality platforms feels all too
                familiar.
            </p>
            <h5>Conclusion</h5>
            <p>
                This story, and the others in Ted Chiang's
                collection <a href="https://www.amazon.com/Exhalation-Ted-Chiang/dp/1101972084/ref=sr_1_1">Exhalation</a>, feel
                very much like <em>Black Mirror</em>, and are worth reading for anyone interested in the somewhat cerebral
                side of speculative fiction.
            </p>
            <p>
                I'm about halfway through the collection, and so far all of the stories have
                been incredibly compelling and thought-provoking (though most are also intensely depressing or
                disturbing--for example, be prepared to think about the nuances of robot sex,
                to meditate on the heat death of the universe, to picture an infant being mangled by
                a breastfeeding robot, etc...).
            </p>
            <p>
                There has been a lot of (fictional and non-fictional) speculation lately about artifial intelligence,
                and for good reason--those outside the industry are likely to underestimate the depth of the impact
                these tools will have on our society. In that sense, the work of authors like Chiang in exploring the
                dynamics of life alongside AI is not mere entertainment, but actual speculation about things that may,
                one way or another, actually come to pass.
            </p>
            <p>
                If you do end up giving it a read, or if you have recommendations for other sci-fi authors I should
                check out, let me know!
            </p>
        </Container>
    )
}

export default TheLifecycleOfSoftwareObjects;
